import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import Logo from '../components/Logo'
import Page from '../components/Page'
import TabBar from '../components/TabBar'

export default function History() {
  const { user } = useAuth()
  const { activeGroup } = useGroup()
  const navigate = useNavigate()
  const [watches, setWatches] = useState([])
  const [myRatings, setMyRatings] = useState({})
  const [stats, setStats] = useState({ total: 0, avgRating: 0, streak: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadHistory()
  }, [user, activeGroup])

  async function loadHistory() {
    setLoading(true)

    // Group history (if there is an active group) + solo history for this user,
    // merged into a single list.
    const tasks = []

    if (activeGroup) {
      tasks.push(
        supabase
          .from('watch_history')
          .select('*, movies(*), profiles!watch_history_suggested_by_fkey(display_name)')
          .eq('group_id', activeGroup.id)
      )
    }

    tasks.push(
      supabase
        .from('watch_history')
        .select('*, movies(*), profiles!watch_history_suggested_by_fkey(display_name)')
        .eq('user_id', user.id)
    )

    const results = await Promise.all(tasks)
    const combined = results.flatMap(r => r.data || [])

    // Deduplicate just in case (user_id and group_id are mutually exclusive
    // per the owner-consistency check, so collisions are unlikely).
    const seen = new Set()
    const unique = combined.filter(w => {
      if (seen.has(w.id)) return false
      seen.add(w.id)
      return true
    })

    unique.sort((a, b) => {
      const ad = a.watched_on || ''
      const bd = b.watched_on || ''
      if (ad === bd) {
        return (b.created_at || '').localeCompare(a.created_at || '')
      }
      return bd.localeCompare(ad)
    })

    setWatches(unique)

    // Calculate stats and load my ratings per watch
    if (unique.length > 0) {
      const watchIds = unique.map(w => w.id)
      const { data: ratings } = await supabase
        .from('ratings')
        .select('score, watch_history_id, user_id')
        .in('watch_history_id', watchIds)

      const avgRating = ratings && ratings.length > 0
        ? Math.round((ratings.reduce((sum, r) => sum + r.score, 0) / ratings.length) * 10) / 10
        : 0

      // Build map of my ratings
      const mine = {}
      ;(ratings || []).forEach(r => {
        if (r.user_id === user.id) mine[r.watch_history_id] = r.score
      })
      setMyRatings(mine)

      setStats({ total: unique.length, avgRating, streak: unique.length })
    } else {
      setMyRatings({})
      setStats({ total: 0, avgRating: 0, streak: 0 })
    }

    setLoading(false)
  }

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <span className="text-[var(--fp-text-muted)]">Loading...</span>
      </div>
    )
  }

  return (
    <Page mode="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between py-4 mb-10">
        <Logo size="md" />
      </div>

      <h1 className="fp-display text-3xl text-white mb-12">Watch History</h1>

      {/* Stats Banner */}
      {watches.length > 0 && (
        <div className="fp-card-elevated mb-14">
          <div className="flex justify-between text-center py-4">
            <div>
              <span className="fp-display text-4xl text-white">{stats.total}</span>
              <br />
              <span className="text-xs text-[var(--fp-text-muted)] mt-2 block">Movies watched</span>
            </div>
            <div>
              <span className="fp-display text-4xl fp-gold-text">{stats.avgRating || '—'}</span>
              <br />
              <span className="text-xs text-[var(--fp-text-muted)] mt-2 block">Avg rating</span>
            </div>
            <div>
              <span className="fp-display text-4xl text-white">{stats.streak}</span>
              <br />
              <span className="text-xs text-[var(--fp-text-muted)] mt-2 block">Total nights</span>
            </div>
          </div>
        </div>
      )}

      {watches.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-[var(--fp-text-muted)]">No movies watched yet. Spin the wheel!</p>
        </div>
      ) : (
        <>
          {watches.map(w => {
            const isSolo = !!w.user_id && !w.group_id
            return (
              <div
                key={w.id}
                onClick={() => navigate('/rate', { state: { watch: w } })}
                className="fp-card flex gap-6 mb-5 cursor-pointer"
              >
                <div className="w-24 h-36 rounded-xl overflow-hidden flex-shrink-0 bg-[var(--fp-border)] shadow-lg">
                  {w.movies?.poster_url ? (
                    <img src={w.movies.poster_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl bg-[var(--fp-surface)]">🎬</div>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center py-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="fp-display text-lg text-white truncate">{w.movies?.title}</h3>
                    {isSolo && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--fp-gold-soft)', color: 'var(--fp-gold)' }}
                      >
                        Solo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--fp-text-muted)] mt-2">
                    {w.watched_on && new Date(w.watched_on + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {!isSolo && w.profiles?.display_name && ` · Suggested by ${w.profiles.display_name}`}
                  </p>
                  {myRatings[w.id] ? (
                    <p className="text-sm mt-3" style={{ color: 'var(--fp-gold)' }}>
                      {'★'.repeat(myRatings[w.id])}<span style={{ color: 'var(--fp-border)' }}>{'★'.repeat(5 - myRatings[w.id])}</span>
                      <span className="text-[var(--fp-text-dim)] ml-2 text-xs">Tap to update</span>
                    </p>
                  ) : (
                    <p className="text-sm text-[var(--fp-red)] mt-3 font-semibold">Tap to rate →</p>
                  )}
                </div>
              </div>
            )
          })}
        </>
      )}

      <TabBar />
    </Page>
  )
}
